import clsx from 'clsx';

/**
 * Initials avatar.
 *
 * The portal does not send a photo in the handshake claims, and fetching one
 * would mean this console reaching back into the portal for decoration. Deriving
 * a mark from the name costs nothing and never 404s.
 *
 * The hue is a stable hash of the email, so a given person is always the same
 * colour — which is what makes an avatar scannable in a list rather than just
 * ornamental. Saturation and lightness are fixed to values that keep white
 * text legible on every hue, in both themes.
 */
export function Avatar({
  name,
  email,
  size = 26,
  className,
}: {
  name: string;
  email: string;
  size?: number;
  className?: string;
}) {
  const initials = initialsOf(name);
  const hue = hueOf(email || name);

  return (
    <span
      className={clsx(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold text-white',
        className,
      )}
      style={{
        width: size,
        height: size,
        // 62% lightness clears 4.5:1 against white at every hue; the darker
        // ring gives the circle an edge on light grounds without a border.
        background: `linear-gradient(145deg, hsl(${hue} 52% 56%), hsl(${(hue + 26) % 360} 56% 44%))`,
        fontSize: Math.round(size * 0.4),
        lineHeight: 1,
      }}
      title={`${name} · ${email}`}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => /[a-z0-9]/i.test(p));

  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  // First and last, not first two — "INSAF P" should read IP, not IN.
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function hueOf(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 360;
}
